import {alpha, styled, useTheme} from "@mui/material/styles";
import {useTranslation} from "react-i18next";
import * as React from "react";
import {useCallback} from "react";
import {BootstrapDialog, Transition} from "../../../../common/components/BootstrapDialog";
import {
    Button,
    DialogActions,
    IconButton,
    Snackbar,
    Stack,
    Switch,
    TextareaAutosize,
    TextField,
    Typography
} from "@mui/material";
import Iconify from "../../../../common/components/iconify";
import Divider from "@mui/material/Divider";
import DialogContent from "@mui/material/DialogContent";
import {LessonOccurrenceDecorator} from "../../../../model/LessonOccurrenceDecorator";
import useMediaQuery from "@mui/material/useMediaQuery";
import {TimetableDecorator} from "../../../../model/TimetableDecorator";
import OccurrenceLayout from "./OccurrenceLayout/OccurrenceLayout";
import SubjectPopover from "../../../../common/components/SubjectPopover";
import {COLOR_DEFAULT} from "../../../../utils/color";
import ColorPopover from "../../../../common/components/color-popover";
import OccurrenceRepeatDialog from "./OccurrenceRepeatDialog";
import SubjectCommitDialog from "../../../task/dialog/SubjectCommitDialog";
import SaveButton from "../../../../common/components/SaveButton";
import {Subject} from "../../../../types/Subject";
import {Lesson} from "../../../../types/Lesson";
import {useLessonCommitDialogLogic} from "./useLessonCommitDialogLogic";


// --- Define Props ---
export interface LessonCommitDialogProps {
    open: boolean;
    handleClose: () => void;
    selectedPlannerId: string;
    selectedTimetable: TimetableDecorator;
    subjects: Subject[] | undefined;
    updateOptions?: {
        lesson: Lesson | undefined;
        occurrences: LessonOccurrenceDecorator[] | undefined;
    } | null;
}

export default function LessonCommitDialog(props: LessonCommitDialogProps) {
    const theme = useTheme();
    const {t} = useTranslation();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const {
        // State
        occurrences,
        selectedSubjectId,
        selectedSubject,
        title,
        color,
        useSubject,
        room,
        note,
        error,

        // UI State
        repeatDialogOccurrence,
        subjectAnchorEl,
        subjectDialogOpen,
        colorAnchorEl,
        subjectDialogUpdateOptions,

        // Callbacks
        setTitle,
        setColor,
        setUseSubject,
        setRoom,
        setNote,
        setOccurrences,
        setSelectedSubjectId,
        setError,
        setRepeatDialogOccurrence,
        setSubjectAnchorEl,
        setSubjectDialogOpen,
        setColorAnchorEl,
        setSubjectDialogUpdateOptions,
        createLessonCallback,
        updateLessonCallback,
        addOccurrence,
        updateOccurrence,
        handleOnOccurrenceDelete,
        getRepeatDialogPattern,
        getRepeatDialogIsRecurring,
        handleOnRepeatChanged,
        deleteSubject,
        handleDateChanged,
        handleDayOfWeekChange,
        handleIndexOfWeekChange,
        handleIndexOfDayChange,
        handleStartTimeInMinutesChange,
        handleEndTimeInMinutesChange,
        handleStartTimeInPeriodsChange,
        handleEndTimeInPeriodsChange,

    } = useLessonCommitDialogLogic(props);

    const handleClose = useCallback(() => {
        setOccurrences([])
        setSelectedSubjectId(undefined)
        setTitle(undefined)
        setUseSubject(true)
        setColor(undefined)
        setRoom(undefined)
        setNote(undefined)

        props.handleClose()
    }, [props.handleClose]);

    const handleSaveClick = useCallback(() => {
        if (props.updateOptions) {
            updateLessonCallback().then(
                () => {
                    handleClose()
                },
                (reason) => {
                    setError(reason.toString())
                }
            )
        } else {
            createLessonCallback().then(
                () => {
                    handleClose()
                },
                (reason) => {
                    setError(reason.toString())
                }
            )
        }
    }, [props.updateOptions, updateLessonCallback, createLessonCallback, handleClose, setError]);

    const subjectOpen = Boolean(subjectAnchorEl);
    const popoverSubjectId = subjectOpen ? 'TimetablePopover' : undefined;

    const colorOpen = Boolean(colorAnchorEl);
    const popoverColorId = colorOpen ? 'color-popover' : undefined;

    return (
        <BootstrapDialog
            fullWidth={true}
            maxWidth='sm'
            TransitionComponent={Transition}
            fullScreen={fullScreen}
            open={props.open}
        >
            <Stack
                direction="row"
                alignItems="center"
                sx={{pt: 1.5, pb: 1.5, pl: 3, pr: 2, backgroundColor: theme.palette.primary.main}}
            >
                <Typography
                    flex={1}
                    fontWeight={600}
                    fontSize={'1.15em'}
                    noWrap={true}
                    color={theme.palette.primary.contrastText}
                    sx={{textTransform: 'capitalize'}}
                >

                    {t(props.updateOptions ? 'timetable.lesson.commit_dialog.title_edit' : 'timetable.lesson.commit_dialog.title_add')}
                </Typography>
                <IconButton onClick={handleClose}>
                    {
                        <Iconify
                            // @ts-ignore
                            icon="material-symbols:close"
                            color={theme.palette.primary.contrastText}
                            width={24}
                            height={24}
                        />
                    }
                </IconButton>
            </Stack>

            <Divider/>

            <DialogContent>
                <Stack direction="column">
                    <Stack
                        direction={fullScreen ? "column" : "row"}
                        alignItems={fullScreen ? 'left' : 'center'}
                    >
                        <Typography noWrap={true} flex={1} variant={'body2'} mr={2}>
                            {t('timetable.lesson.commit_dialog.subject_label')}
                        </Typography>
                        {
                            useSubject && <Button
                                variant={'outlined'}
                                color={'inherit'}
                                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                    setSubjectAnchorEl(event.currentTarget);
                                }}
                                sx={{
                                    mt: fullScreen ? 1 : 0,
                                    py: '8.5px'
                                }}
                            >
                                {
                                    selectedSubject && <Iconify
                                        // @ts-ignore
                                        icon="material-symbols:circle"
                                        color={selectedSubject?.color ?? theme.palette.text.secondary}
                                        width={14}
                                        height={14}
                                        mr={1}
                                    />
                                }
                                <Typography variant={'body2'}>
                                    {
                                        selectedSubject ? selectedSubject.name : t('choose')
                                    }
                                </Typography>
                            </Button>
                        }
                        {
                            !useSubject && <TextField
                                variant={'outlined'}
                                size={'small'}
                                value={title}
                                placeholder={'Subject name'}
                                sx={{
                                    mt: fullScreen ? 1 : 0,
                                    '& .MuiOutlinedInput-input': {
                                        py: '10.5px',
                                        fontSize: theme.typography.body2.fontSize
                                    }
                                }}
                                onChange={(event) => {
                                    setTitle(event.target.value)
                                }}
                            />
                        }
                    </Stack>

                    {
                        !useSubject && <Stack direction={'row'} alignItems={'center'} mt={2}>
                            <Typography noWrap={true} flex={1} variant={'body2'} mr={2}>
                                {t('timetable.lesson.commit_dialog.color_label')}
                            </Typography>
                            <Button
                                variant={'outlined'}
                                color={'inherit'}
                                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                    setColorAnchorEl(event.currentTarget);
                                }}
                                sx={{py: '8.5px'}}
                            >
                                <Iconify
                                    // @ts-ignore
                                    icon="material-symbols:circle"
                                    color={color}
                                    width={14}
                                    height={14}
                                    mr={1}
                                />
                                <Typography variant={'body2'}>
                                    {t('timetable.lesson.commit_dialog.pick_color')}
                                </Typography>
                            </Button>
                        </Stack>
                    }

                    <Stack direction={'row'} alignItems={'center'} mt={2}>
                        <Typography noWrap={true} flex={1} variant={'body2'} mr={2}>
                            {t('timetable.lesson.commit_dialog.use_subject_label')}
                        </Typography>
                        <Switch
                            value={useSubject}
                            checked={useSubject}
                            onChange={(event) => {
                                setUseSubject(event.target.checked)
                            }}
                        />
                    </Stack>

                    <Divider sx={{mt: 2}}/>

                    {
                        occurrences.map((occurrence, index) => {
                            return (
                                <OccurrenceLayout
                                    index={index}
                                    key={occurrence.getId()}
                                    timetable={props.selectedTimetable}
                                    occurrence={occurrence}
                                    // Date
                                    handleDateChange={(date) => {
                                        handleDateChanged(occurrence.getId(), date)
                                    }}
                                    handleDayOfWeekChange={(dayOfWeek) => {
                                        handleDayOfWeekChange(occurrence.getId(), dayOfWeek)
                                    }}
                                    handleIndexOfWeekChange={(indexOfWeek) => {
                                        handleIndexOfWeekChange(occurrence.getId(), indexOfWeek)
                                    }}
                                    handleIndexOfDayChange={(indexOfDay) => {
                                        handleIndexOfDayChange(occurrence.getId(), indexOfDay)
                                    }}
                                    // Time
                                    handleStartTimeInMinutesChange={(timeInMinutes) => {
                                        handleStartTimeInMinutesChange(occurrence.getId(), timeInMinutes)
                                    }}
                                    handleEndTimeInMinutesChange={(timeInMinutes) => {
                                        handleEndTimeInMinutesChange(occurrence.getId(), timeInMinutes)
                                    }}
                                    handleStartTimeInPeriodsChange={(timeInPeriods) => {
                                        handleStartTimeInPeriodsChange(occurrence.getId(), timeInPeriods)
                                    }}
                                    handleEndTimeInPeriodsChange={(timeInPeriods) => {
                                        handleEndTimeInPeriodsChange(occurrence.getId(), timeInPeriods)
                                    }}
                                    // Events
                                    handleOnOccurrenceDelete={() => {
                                        handleOnOccurrenceDelete(occurrence.getId())
                                    }}
                                    handleOnUpdateRepeat={() => {
                                        setRepeatDialogOccurrence(occurrence)
                                    }}
                                />
                            )
                        })
                    }

                    <Stack direction={'row'} alignItems={'center'} mt={2}>
                        <Button
                            color={'inherit'}
                            variant={'outlined'}
                            sx={{pl: 1, pr: 1.5}}
                            onClick={() => addOccurrence()}
                        >
                            <Iconify
                                // @ts-ignore
                                icon="material-symbols:add"
                                color={theme.palette.text.primary}
                                width={18}
                                height={18}
                                mr={0.5}
                            />
                            <Typography variant={'body2'}>
                                {t('timetable.lesson.commit_dialog.occurrence.add_multiple')}
                            </Typography>
                        </Button>
                    </Stack>

                    <Divider sx={{mt: 2}}/>

                    <Stack
                        direction={fullScreen ? "column" : "row"}
                        alignItems={fullScreen ? 'left' : 'center'}
                        mt={2}
                    >
                        <Typography noWrap={true} flex={1} variant={'body2'} mr={2}>
                            {t('timetable.lesson.commit_dialog.room_label')}
                        </Typography>
                        <TextField
                            variant={'outlined'}
                            size={'small'}
                            value={room}
                            placeholder={t('timetable.lesson.commit_dialog.room_label') ?? ''}
                            sx={{
                                mt: fullScreen ? 1 : 0,
                                '& .MuiOutlinedInput-input': {
                                    py: '10.5px',
                                    fontSize: theme.typography.body2.fontSize
                                }
                            }}
                            onChange={(event) => {
                                setRoom(event.target.value)
                            }}
                        />
                    </Stack>

                    <Stack
                        direction="row"
                        mt={3}
                        style={{
                            border: `1px solid ${alpha(theme.palette.grey[500], 0.32)}`,
                            borderRadius: '8px'
                        }}
                    >
                        <NoteTextArea
                            placeholder={t('task_dialog.add_note') || ''}
                            value={note || ''}
                            onChange={(event: React.ChangeEvent) => {
                                // @ts-ignore
                                setNote(event.target.value)
                            }}
                        />
                    </Stack>

                </Stack>
            </DialogContent>

            <Divider/>

            <DialogActions>
                <Stack
                    direction='row-reverse'
                    spacing={1.5}
                    sx={{marginTop: '8px', marginBottom: '12px', marginRight: '12px'}}
                >
                    <SaveButton
                        handleSaveClick={handleSaveClick}
                        isSaving={false}
                        text={t("save")}/>
                    <Button
                        onClick={handleClose}
                        variant='contained'
                        color='inherit'
                        sx={{boxShadow: 'none'}}
                    >
                        {t("cancel")}
                    </Button>
                </Stack>
            </DialogActions>

            <SubjectPopover
                id={popoverSubjectId}
                subjects={props.subjects}
                open={subjectOpen}
                anchorEl={subjectAnchorEl}
                handleClose={() => {
                    setSubjectAnchorEl(null)
                }}
                handleOnSubjectAdd={() => {
                    setSubjectAnchorEl(null)
                    setSubjectDialogOpen(true)
                }}
                handleOnSubjectUpdate={(subjectId: string) => {
                    setSubjectAnchorEl(null)

                    const subject = props.subjects?.find(it => it.id === subjectId)
                    if (subject) {
                        setSubjectDialogUpdateOptions({subject: subject});
                        setSubjectDialogOpen(true)
                    }
                }}
                handleOnSubjectSelect={(subjectId) => {
                    setSubjectAnchorEl(null)
                    setSelectedSubjectId(subjectId)
                }}
                handleOnSubjectDelete={(subjectId: string) => {
                    setSubjectAnchorEl(null)
                    setSelectedSubjectId(null)

                    deleteSubject(subjectId);
                }}
            />

            <SubjectCommitDialog
                plannerId={props.selectedPlannerId}
                open={subjectDialogOpen}
                updateOptions={subjectDialogUpdateOptions}
                handleClose={() => {
                    setSubjectDialogOpen(false)
                    setSubjectDialogUpdateOptions(null)
                }}
            />

            <ColorPopover
                id={popoverColorId}
                color={color ?? COLOR_DEFAULT}
                open={colorOpen}
                anchorEl={colorAnchorEl}
                handleClose={() => setColorAnchorEl(null)}
                onChange={(color) => setColor(color.hex)}
            />

            <OccurrenceRepeatDialog
                open={!!repeatDialogOccurrence}
                handleClose={() => setRepeatDialogOccurrence(undefined)}
                timetable={props.selectedTimetable}
                isRecurring={getRepeatDialogIsRecurring()}
                pattern={getRepeatDialogPattern()}
                handleOnRepeatChanged={(isRecurring, pattern) => {
                    updateOccurrence(repeatDialogOccurrence?.getId() || '', it => {
                        it.setRecurring(isRecurring);
                        it.setRecurringPattern(pattern?.get());
                    })
                }}
            />

            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => {
                    setError(undefined)
                }}
                message={error}
            />

        </BootstrapDialog>
    )
}


// ---------------------------------------------------------------------------------------------------------------------
const NoteTextArea = styled(TextareaAutosize)(({theme}) => ({
    flex: 1,
    margin: '0px 16px',
    paddingTop: '12px',
    paddingBottom: '12px',
    border: 'none',
    outline: 'none',
    resize: 'none',
    minHeight: '48px',
    color: theme.palette.text.secondary,
    fontFamily: theme.typography.body2.fontFamily,
    fontSize: theme.typography.body2.fontSize,
    fontWeight: theme.typography.body2.fontWeight
}));